/**
 * Air (§1.7): bomber strikes / scouting, fighter patrols, auto-scramble intercepts,
 * air-to-air engagements, landing and rearm hand-off.
 */
import { BALANCE } from '../balance.js';
import {
  addEntity,
  advance,
  aircraftOf,
  aircraftSpeed,
  allied,
  isAircraft,
  isAirborne,
  removeEntity,
  setCourse,
  shipActive,
  staticSeg,
  stopAt,
  structureActive,
} from '../entities.js';
import { nextFloat } from '../rng.js';
import type { AircraftEntity, AirMission, BombEntity, Entity, MatchState, SimEvent } from '../types.js';

const F = BALANCE.fighter;
const B = BALANCE.bomber;

function homeOf(state: MatchState, a: AircraftEntity): Entity | undefined {
  return state.entities.get(a.baseId);
}

/** Send a parked aircraft on a mission toward (x, y). */
export function launchAircraft(state: MatchState, a: AircraftEntity, mission: AirMission, x: number, y: number): void {
  a.mission = mission;
  a.phase = 'outbound';
  a.tx = x;
  a.ty = y;
  a.targetId = 0;
  setCourse(a, x, y, aircraftSpeed(a), state.tick);
}

export function recallAircraft(state: MatchState, a: AircraftEntity): void {
  const base = homeOf(state, a);
  a.phase = 'returning';
  a.targetId = 0;
  if (base) setCourse(a, base.x, base.y, aircraftSpeed(a), state.tick);
  a.steerTick = state.tick + F.steerTicks;
}

export function retargetAircraft(state: MatchState, a: AircraftEntity, x: number, y: number): void {
  a.tx = x;
  a.ty = y;
  const goOut = a.mission === 'patrol' || a.mission === 'scout' || (a.mission === 'strike' && a.ammo > 0);
  if (goOut) {
    a.phase = 'outbound';
    a.targetId = 0;
    setCourse(a, x, y, aircraftSpeed(a), state.tick);
  }
}

export function airSystem(state: MatchState, events: SimEvent[]): void {
  const tick = state.tick;
  const landed: AircraftEntity[] = [];
  for (const e of state.entities.values()) {
    if (!isAircraft(e)) continue;
    if (!isAirborne(e)) {
      // parked / building / rearming aircraft ride with their (possibly moving) base
      const base = homeOf(state, e);
      if (base && (base.x !== e.x || base.y !== e.y)) stopAt(e, base.x, base.y, tick);
      continue;
    }
    const speed = aircraftSpeed(e);
    switch (e.phase) {
      case 'outbound':
        if (advance(e, e.tx, e.ty, speed, tick)) arrive(state, e);
        break;
      case 'onStation':
        if (tick >= e.stationUntil) recallAircraft(state, e);
        break;
      case 'intercept': {
        const t = state.entities.get(e.targetId);
        const base = homeOf(state, e);
        if (!t || !isAircraft(t) || !isAirborne(t) || !((t.visMask >> e.owner) & 1) || !base || dist2(base, e) > F.radius ** 2) {
          recallAircraft(state, e);
          break;
        }
        if (tick >= e.steerTick) {
          setCourse(e, t.x, t.y, speed, tick);
          e.steerTick = tick + F.steerTicks;
        }
        advance(e, t.x, t.y, speed, tick);
        break;
      }
      case 'returning': {
        const base = homeOf(state, e);
        if (!base) break;
        if (tick >= e.steerTick && base.kind === 'carrier') {
          setCourse(e, base.x, base.y, speed, tick);
          e.steerTick = tick + F.steerTicks;
        }
        if (advance(e, base.x, base.y, speed, tick) || dist2(base, e) <= BALANCE.aircraft.landRadius ** 2) landed.push(e);
        break;
      }
      default:
        break;
    }
    if (e.kind === 'fighter' && e.ammo > 0 && tick >= e.nextShotTick && state.entities.has(e.id)) fighterEngage(state, e, events);
  }
  for (const a of landed) land(state, a, events);
  if (tick % BALANCE.airbase.scrambleCheckTicks === 0) autoScramble(state);
}

function dist2(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

function arrive(state: MatchState, a: AircraftEntity): void {
  if (a.mission === 'strike' && a.kind === 'bomber' && a.ammo > 0) {
    a.ammo = 0;
    addEntity<BombEntity>(state, (id) => ({
      id,
      kind: 'bomb',
      owner: a.owner,
      x: a.tx,
      y: a.ty,
      createdTick: state.tick,
      visMask: 0,
      bomberId: a.id,
      impactTick: state.tick + B.bombFallTicks,
    }));
    state.stats.players[a.owner - 1].bombsDropped++;
    recallAircraft(state, a);
    return;
  }
  if (a.mission === 'patrol') {
    a.phase = 'onStation';
    a.stationUntil = state.tick + F.stationTicks;
    stopAt(a, a.tx, a.ty, state.tick);
    return;
  }
  recallAircraft(state, a);
}

function land(state: MatchState, a: AircraftEntity, events: SimEvent[]): void {
  const base = homeOf(state, a);
  if (!base) return;
  a.x = base.x;
  a.y = base.y;
  a.seg = staticSeg(base.x, base.y, state.tick);
  a.mission = null;
  a.targetId = 0;
  events.push({ type: 'landed', id: a.id, kind: a.kind, owner: a.owner });
  if (a.kind === 'fighter') {
    if (a.ammo < F.aams) {
      a.phase = 'rearming';
      a.readyTick = state.tick + F.rearmTicks;
    } else a.phase = 'parked';
  } else if (a.ammo < 1) {
    // bomber rearm is paid by the build system as soon as credits allow
    a.phase = 'rearming';
    a.readyTick = 0;
  } else a.phase = 'parked';
}

function fighterEngage(state: MatchState, f: AircraftEntity, events: SimEvent[]): void {
  const r2 = F.engageRange ** 2;
  let target: AircraftEntity | null = null;
  let best = Infinity;
  for (const t of state.entities.values()) {
    if (!isAircraft(t) || !isAirborne(t) || t.owner === f.owner || allied(state, f.owner, t.owner)) continue;
    if (!((t.visMask >> f.owner) & 1)) continue;
    const d = dist2(t, f);
    if (d <= r2 && d < best) {
      best = d;
      target = t;
    }
  }
  if (!target) return;
  f.ammo--;
  f.nextShotTick = state.tick + F.shotCooldownTicks;
  const pk = target.kind === 'bomber' ? F.pkBomber : F.pkFighter;
  const hit = nextFloat(state.combatRng) < pk;
  const base = { shooterId: f.id, shooterOwner: f.owner, targetId: target.id, fireTick: state.tick, resolveTick: state.tick, where: { sx: f.x, sy: f.y, tx: target.x, ty: target.y } };
  events.push({ type: 'interceptFire', ...base });
  events.push({ type: 'interceptResolve', ...base, hit });
  if (hit) removeEntity(state, target.id, 'destroyed', events);
  if (f.ammo === 0 && f.phase !== 'returning') recallAircraft(state, f);
}

/** Bases with auto-scramble launch up to 2 fighters at each visible hostile bomber within 30 mu. */
function autoScramble(state: MatchState): void {
  const r2 = BALANCE.airbase.scrambleRange ** 2;
  const chased = new Set<number>();
  for (const e of state.entities.values()) if (isAircraft(e) && e.phase === 'intercept') chased.add(e.targetId);
  for (const base of state.entities.values()) {
    if (base.kind === 'airbase') {
      if (!base.autoScramble || !structureActive(state, base)) continue;
    } else if (base.kind === 'carrier') {
      if (!base.autoScramble || !shipActive(state, base)) continue;
    } else continue;
    let ready: AircraftEntity[] | null = null;
    for (const t of state.entities.values()) {
      if (t.kind !== 'bomber' || !isAirborne(t) || chased.has(t.id) || allied(state, base.owner, t.owner)) continue;
      if (!((t.visMask >> base.owner) & 1) || dist2(t, base) > r2) continue;
      ready ??= aircraftOf(state, base.id).filter((a) => a.kind === 'fighter' && a.phase === 'parked' && a.ammo > 0);
      const n = Math.min(BALANCE.airbase.scrambleCount, ready.length);
      if (n === 0) break;
      for (const f of ready.splice(0, n)) {
        f.mission = 'patrol';
        f.phase = 'intercept';
        f.targetId = t.id;
        f.steerTick = state.tick + F.steerTicks;
        setCourse(f, t.x, t.y, F.speed, state.tick);
      }
      chased.add(t.id);
    }
  }
}
